import React from "react";
import { Link } from "react-router-dom";
import { cash, dateRange, longDate, nightsLabel, daysNights, parseDate } from "../../../services/api";
import StayCalendar from "../StayCalendar";

const dayBefore = (d, n) => { const x = parseDate(d); x.setDate(x.getDate() - n); return x; };
const dayAfter = (d, n) => { const x = parseDate(d); x.setDate(x.getDate() + n); return x; };
const fmt = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

/**
 * Step 1 — the dates are the retreat's own and the whole retreat is
 * booked; what the guest chooses is how many are coming, and any extra
 * nights the team has opened on either side.
 */
const StepDates = ({ d, set, options, retreat }) => {
  const ext = retreat.extension;
  const maxParty = Math.min(retreat.maxParty, retreat.placesLeft);
  const nightPrice = ext.nightPrice;

  return (
    <section className="bk-step" aria-labelledby="s1">
      <h2 id="s1" className="bk-step-title">Your dates</h2>
      <p className="mk-muted">Everyone attends the whole retreat. You can add nights before or after where we have them open.</p>

      <div className="bk-grid-2">
        <div>
          <div className="bk-panel bk-fixed-dates">
            <p className="bk-label">Retreat dates</p>
            <p className="bk-big">{dateRange(retreat.startDate, retreat.endDate)}</p>
            <p className="bk-muted">{daysNights(retreat.nights)} · arrive {longDate(retreat.startDate)}, leave {longDate(retreat.endDate)}</p>
            <p className={`bk-left ${retreat.placesLeft <= 3 ? "is-low" : ""}`}>{retreat.placesLeft} {retreat.placesLeft === 1 ? "place" : "places"} left</p>
          </div>

          <fieldset className="bk-panel">
            <legend className="bk-label">How many of you?</legend>
            <div className="bk-stepper">
              <button type="button" aria-label="One fewer guest" disabled={d.guests <= 1} onClick={() => set({ guests: d.guests - 1 })}>−</button>
              <output aria-live="polite">{d.guests} {d.guests === 1 ? "guest" : "guests"}</output>
              <button type="button" aria-label="One more guest" disabled={d.guests >= maxParty} onClick={() => set({ guests: d.guests + 1 })}>+</button>
            </div>
            <p className="bk-muted">
              Up to {maxParty} can book together here.{" "}
              {maxParty < retreat.maxParty ? "That's every place left. " : ""}
              For a bigger group, <Link to="/contact">get in touch</Link>.
            </p>
          </fieldset>

          {(ext.maxBefore > 0 || ext.maxAfter > 0) && (
            <fieldset className="bk-panel">
              <legend className="bk-label">Extra nights <em>optional</em></legend>
              {ext.maxBefore > 0 && (
                <label className="bk-check">
                  <select value={d.before} onChange={(e) => set({ before: Number(e.target.value) })} aria-label="Nights before the retreat">
                    {Array.from({ length: ext.maxBefore + 1 }, (_, n) => <option key={n} value={n}>{n === 0 ? "No nights before" : `${nightsLabel(n)} before`}</option>)}
                  </select>
                  <span>{d.before > 0 ? `Arrive ${fmt(dayBefore(retreat.startDate, d.before))}` : `Arrive on ${fmt(parseDate(retreat.startDate))}`}</span>
                </label>
              )}
              {ext.maxAfter > 0 && (
                <label className="bk-check">
                  <select value={d.after} onChange={(e) => set({ after: Number(e.target.value) })} aria-label="Nights after the retreat">
                    {Array.from({ length: ext.maxAfter + 1 }, (_, n) => <option key={n} value={n}>{n === 0 ? "No nights after" : `${nightsLabel(n)} after`}</option>)}
                  </select>
                  <span>{d.after > 0 ? `Leave ${fmt(dayAfter(retreat.endDate, d.after))}` : `Leave on ${fmt(parseDate(retreat.endDate))}`}</span>
                </label>
              )}
              <p className="bk-muted">
                {nightPrice > 0 ? `${cash(nightPrice)} per guest per night, plus any room upgrade. ` : ""}
                Your room is yours for the whole stay, so you never change rooms.
              </p>
            </fieldset>
          )}

          {ext.requests && (
            <label className="mk-field bk-panel">
              <span>Need different nights? <em>optional</em></span>
              <textarea rows={2} value={d.extension_request} maxLength={500}
                onChange={(e) => set({ extension_request: e.target.value })}
                placeholder="For example: arriving three days early" />
              <small className="bk-muted">This is a request, not a booking. We'll check with Cerro Tusa Springs and come back to you.</small>
            </label>
          )}
        </div>

        <StayCalendar start={retreat.startDate} end={retreat.endDate} before={d.before} after={d.after}
          maxBefore={ext.maxBefore} maxAfter={ext.maxAfter} />
      </div>

      {options.otherDates.length > 0 && (
        <div className="bk-other">
          <p className="bk-label">Other dates for this retreat</p>
          <ul>
            {options.otherDates.map((o) => (
              <li key={o.id}>
                <Link to={`/book/${o.slug}`}>
                  <b>{dateRange(o.startDate, o.endDate)}</b>
                  <span>{nightsLabel(o.nights)} · {o.soldOut ? "full" : `${o.placesLeft} left`}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default StepDates;
